import { memo, useCallback, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { PromptTextarea } from './PromptTextarea';
import { SectionRow } from './SectionContainer';
import { Button } from '~/components/ui/Button';
import { NOOP } from '~/utils/noops';
import { withSpinner } from '~/utils/withSpinner';

interface PromptEditorProps {
  value: string;
  placeholder?: string;
  description: string;
  disabled?: boolean;
  saveDisabled?: boolean;
  innerClassName?: string;
  setValue: (value: string) => void;
  onSave: (value: string) => Promise<void>;
}

export const PromptEditor = memo<PromptEditorProps>(
  ({
    value,
    placeholder = 'Prompt here...',
    disabled = false,
    saveDisabled,
    description,
    onSave,
    setValue,
    innerClassName = 'h-full',
  }) => {
    const [saving, setSaving] = useState(false);

    const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
    };

    const handleOnSave = useCallback(
      (value: string) => {
        withSpinner({
          action: async () => {
            return onSave(value);
          },
          onStateUpdate: setSaving,
        }).catch(NOOP);
      },
      [onSave],
    );

    return (
      <SectionRow
        layout="vertical"
        className="h-full flex-1 min-h-0"
        innerClassName={innerClassName}
        description={description}
        action={
          <div className="flex size-full flex-col gap-2.5">
            <PromptTextarea
              value={value}
              placeholder={placeholder}
              disabled={disabled}
              onChange={handleOnChange}
              onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
                if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                  event.preventDefault();
                  handleOnSave(value);
                }
              }}
            />
            <Button
              className="ml-auto gap-1.5"
              size="md"
              onClick={() => handleOnSave(value)}
              disabled={saving || saveDisabled}
              loading={saving}
            >
              Save prompt
            </Button>
          </div>
        }
      />
    );
  },
);

PromptEditor.displayName = 'PromptEditor';
